import axios from 'axios';
import createLogger from '../utils/logger';
import { API_CONFIG } from '../config/api';

const logger = createLogger('apiClient');

export class ApiError extends Error {
  constructor(message, status, data) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

// Create axios instance with config
// Default baseURL to '/api' in development so requests go through the Vite proxy.
// If a baseURL is provided via config, honor it.
const resolvedBaseURL = API_CONFIG.baseURL ? `${API_CONFIG.baseURL}/api` : '/api';
export const apiClient = axios.create({
  baseURL: resolvedBaseURL,
  timeout: API_CONFIG.timeout,
  headers: API_CONFIG.headers,
  withCredentials: true, // Include cookies with every request
});

// Request counter for rate limiting
let requestCount = 0;
const requestTimestamps = [];

// Custom event for AI usage updates
export const AI_USAGE_UPDATE_EVENT = 'ai-usage-update';
const aiUsageEventEmitter = new EventTarget();

export const subscribeToAIUsageUpdates = (callback) => {
  const handler = () => callback();
  aiUsageEventEmitter.addEventListener(AI_USAGE_UPDATE_EVENT, handler);
  return () => aiUsageEventEmitter.removeEventListener(AI_USAGE_UPDATE_EVENT, handler);
};

// Add request interceptor for common headers, auth, etc.
apiClient.interceptors.request.use(
  async (config) => {
    if (import.meta.env.DEV && typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const testUser = params.get('test-user');
      if (testUser) {
        config.headers = {
          ...(config.headers || {}),
          'x-test-user': testUser
        };
      }
    }

    // Rate limiting
    if (API_CONFIG.rateLimit.enabled) {
      const now = Date.now();
      requestTimestamps.push(now);
      requestTimestamps.splice(0, requestTimestamps.length - API_CONFIG.rateLimit.maxRequestsPerMinute);
      
      if (requestTimestamps.length === API_CONFIG.rateLimit.maxRequestsPerMinute) {
        const windowStart = requestTimestamps[0];
        if (now - windowStart < 60000) { // Less than 1 minute
          throw new ApiError('Rate limit exceeded', 429);
        }
      }
    }

    // Log request
    if (API_CONFIG.logging.enabled) {
      logger.debug(`[apiClient] BaseURL: ${resolvedBaseURL} | Request: ${config.method?.toUpperCase()} ${config.url}`, config);
    }

    return config;
  },
  (error) => {
    logger.error('Request error:', error);
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling, etc.
apiClient.interceptors.response.use(
  (response) => {
    // Log response
    if (API_CONFIG.logging.enabled) {
      logger.debug(`Response: ${response.status} ${response.config.url}`, response.data);
    }

    // Emit AI usage update event if the response contains AI usage data
    if (response.data && response.data.aiUsage) {
      aiUsageEventEmitter.dispatchEvent(new Event(AI_USAGE_UPDATE_EVENT));
    }

    return response;
  },
  (error) => {
    // Log error
    if (API_CONFIG.logging.enabled) {
      logger.error('Response error:', error);
    }

    // Transform error to ApiError
    if (error.response) {
      const { status, data } = error.response;
      const message = data.message || data.error || `API error: ${status}`;
      throw new ApiError(message, status, data);
    } else if (error.request) {
      throw new ApiError('No response received from server', 0);
    } else {
      throw new ApiError(error.message, 0);
    }
  }
);
